import { ActionTree } from 'vuex'
import RootState from '@/store/RootState'
import * as types from './mutation-types'
import logger from '@/logger'
import { hasError } from '@/adapter'
import StockState from './StockState'
import { StockService } from '@/services/StockService'
import { showToast } from '@/utils'
import { translate } from '@/i18n'

const actions: ActionTree<StockState, RootState> = {
  /**
   * Fetch Stockmove
   */
  async fetchStockMoveTask({ commit }, { payload }) {
    const params = {
      "viewSize": payload?.viewSize || 20,
      "viewIndex": payload?.viewIndex || 0,
      "statusId": "STKMV_ASSIGNED",
      "facilityId": this.state.user?.currentFacility?.facilityId ?? "",
      "assignedPartyId": this.state.user?.current?.partyId ?? ""
    }
    let resp;
    try {
      resp= await StockService.fetchStocksMoveTaskDetails(params)
      if (!hasError(resp)) {
        const stockMoveTask = resp.data;
        commit(types.STOCK_ADD_TO_CACHED_MULTIPLE, { stockMoveTask });
      }
      else {
        throw resp.data;
      }
    }
    catch (err) {
      showToast(translate("Failed to fetch the stock move orders"));
      logger.error('Something Went wrong while fetching stockMoveTask', err);
    }
    return resp;
  },

  async fetchStockMoveTaskList({ commit }, payload) {
    let resp;
    let stockMoveTask = [];
    const productIds: Array<string> = [];

    try {
      resp = await StockService.fetchTasksList(payload)
      if (!hasError(resp) && resp.data.docs?.length) {
        stockMoveTask = resp.data.docs;
        // TODO: need to add check for viewIndex merging for inifinte scroll and implement infinite scroll
      } else {
        throw resp.data;
      }
    } catch(err) {
      logger.error("Something Went wrong while fetching stockMoveTask", err);
    } finally {
      stockMoveTask = stockMoveTask.reduce((tasks: any, task: any) => {
        if(tasks[task.workEffortId] && tasks[task.workEffortId]["StockMoveItems"]) {
          tasks[task.workEffortId]["StockMoveItems"].push(task)
        } else {
          tasks[task.workEffortId] = {
            ...task,
            "StockMoveItems": [task]
          }
        }

        productIds.push(task.productId)

        return tasks
      }, {})

      this.dispatch('product/fetchProducts', { productIds: [...new Set(productIds)] })

      commit(types.STOCK_LIST_UPDATED, { tasks: stockMoveTask, total: resp?.data?.count ? resp.data.count : 0, type: payload.inputFields.isFrozen === "Y" ? "cold" : "dry" });
    }
    return resp;
  },

  // Action to fetch the stock move data on the details page
  async fetchStockMoveDetail({ commit }, payload) {
    if(!payload.workEffortId) {
      showToast(translate("Unable to fetch task details, work effort id is missing"))
      logger.error("Unable to fetch task details, work effort id is missing");
      return;
    }

    const params = {
      "viewSize": 20,         
      "viewIndex": 0,
      "workEffortId": payload.workEffortId
    }

    try {
      const resp = await StockService.fetchStocksMoveTaskDetails(params)
      if(!hasError(resp) && resp.data?.stockMoveTasks?.length) {
        // Passing 0th index as we will have a single task for the passed workEffortId
        commit(types.STOCK_DETAILS_UPDATED, resp.data.stockMoveTasks[0]);
      } else {
        throw resp.data;
      }
    } catch (err) {
      showToast(translate("Unable to fetch task details"))
      logger.error("Something went wrong while fetching task details", err);
    }
  },

  async completeStockMoveTask({ state }, payload ) {
    if(!payload.workEffortId){
      showToast(translate("Unable to fetch StockMoveTask details. WorkEffortId is needed"))
      return;
    }
  
    const params={
      "workEffortId": payload.workEffortId,
      "stockMoveItems": payload.stockMoveData
    }
    
    let resp;
    try {
      resp = await StockService.completeStockMoveTask(params)
      if (!hasError(resp)) {
        showToast(translate("Task marked as completed"));
      } else {
        throw resp.data;
      }
    } catch(err) {
      showToast(translate("Failed to mark the stock move task complete"));
      logger.error('Something Went wrong while compeleting the StockMoveTask', err);
    }
    return resp;
  },

  updateTaskDetails({ commit }, payload) {
    commit(types.STOCK_DETAILS_UPDATED, payload);
  },

  async clearTasks({ commit }) {
    commit(types.STOCK_LIST_UPDATED, { tasks: [], total: 0, type: 'cold' });
    commit(types.STOCK_LIST_UPDATED, { tasks: [], total: 0, type: 'dry' });
    commit(types.STOCK_DETAILS_UPDATED, []);
  }
}
export default actions;
